/**
 * MarkdownWithTables — section text as a reader sees it, HTML tables included.
 *
 * A table with merged cells copied from a library source stays an HTML block
 * in the draft (`csr_sim/html_tables.py`), and react-markdown shows raw HTML
 * as literal tags. Each block is lifted out (`extractHtmlTables`) and drawn
 * here as a real `<table>` from its parsed cells, merges as
 * `colSpan`/`rowSpan` — no HTML from the text is ever injected. The rest is
 * ordinary Markdown, where a `<br>` inside a pipe-table cell becomes a line
 * break (`rehypeCellBreaks`) and every other tag stays text.
 */

import type React from "react";
import { Fragment, useMemo } from "react";
import ReactMarkdown, { type Options as ReactMarkdownOptions } from "react-markdown";
import { extractHtmlTables, HTML_TABLE_SENTINEL } from "../../services/formatter/htmlTable";
import type { StructuredTableData } from "../../services/formatter/types";

interface HastNode {
  type: string;
  value?: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

const RE_BR = /^<br\s*\/?>$/i;

/** Rehype plugin: a raw `<br>` (and only that tag) becomes a line break. */
export function rehypeCellBreaks() {
  const walk = (node: HastNode) => {
    node.children?.forEach((child, i, all) => {
      if (child.type === "raw" && RE_BR.test((child.value ?? "").trim())) {
        all[i] = { type: "element", tagName: "br", properties: {}, children: [] };
      } else {
        walk(child);
      }
    });
  };
  return (tree: HastNode) => walk(tree);
}

/** A structured table as a React table: v_merge "restart" cells span down
 *  over the "continue" cells below them, which are not drawn. */
export const StructuredTableView: React.FC<{ data: StructuredTableData }> = ({ data }) => {
  const rows = data.rows.map((r) => r.cells ?? []);
  // Physical start column of each cell, per row.
  const starts = rows.map((cells) => {
    let col = 0;
    return cells.map((c) => {
      const at = col;
      col += c.grid_span && c.grid_span > 0 ? c.grid_span : 1;
      return at;
    });
  });
  const rowSpan = (r: number, col: number): number => {
    let n = 1;
    for (let rr = r + 1; rr < rows.length; rr++) {
      const i = starts[rr].indexOf(col);
      if (i < 0 || rows[rr][i].v_merge !== "continue") break;
      n++;
    }
    return n;
  };
  return (
    <table>
      <tbody>
        {rows.map((cells, r) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity beyond their position
          <tr key={r}>
            {cells.map((c, i) => {
              if (c.v_merge === "continue") return null;
              const Tag = c.bold ? "th" : "td";
              const span = c.grid_span && c.grid_span > 1 ? c.grid_span : undefined;
              const down = c.v_merge === "restart" ? rowSpan(r, starts[r][i]) : 1;
              const lines = (c.text ?? "").split("\n");
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: cells have no identity beyond their position
                <Tag key={i} colSpan={span} rowSpan={down > 1 ? down : undefined}>
                  {lines.map((line, k) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: lines have no identity beyond their position
                    <Fragment key={k}>
                      {k > 0 ? <br /> : null}
                      {line}
                    </Fragment>
                  ))}
                </Tag>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export interface MarkdownWithTablesProps {
  children: string;
  remarkPlugins?: ReactMarkdownOptions["remarkPlugins"];
}

export const MarkdownWithTables: React.FC<MarkdownWithTablesProps> = ({ children, remarkPlugins }) => {
  const parts = useMemo(() => {
    const { text, tables } = extractHtmlTables(children);
    if (tables.length === 0) return [{ md: children }];
    const out: Array<{ md: string } | { table: StructuredTableData }> = [];
    let md: string[] = [];
    for (const line of text.split("\n")) {
      const m = HTML_TABLE_SENTINEL.exec(line.trim());
      if (!m) {
        md.push(line);
        continue;
      }
      if (md.join("").trim()) out.push({ md: md.join("\n") });
      md = [];
      out.push({ table: tables[Number(m[1])] });
    }
    if (md.join("").trim()) out.push({ md: md.join("\n") });
    return out;
  }, [children]);
  return (
    <>
      {parts.map((p, i) =>
        "table" in p ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: parts are positional
          <StructuredTableView key={i} data={p.table} />
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: parts are positional
          <ReactMarkdown key={i} remarkPlugins={remarkPlugins} rehypePlugins={[rehypeCellBreaks]}>
            {p.md}
          </ReactMarkdown>
        ),
      )}
    </>
  );
};
